import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { setMembershipPin } from "../../../APICalling/APICalling";
import { showAppToast } from "../../../Utils/appToast";
import styles from "./SetMembershipPasswordPopup.module.css";
import {
	getMembershipPinInputProps,
	MembershipPinAutofillTrap,
} from "./membershipPinInput";

const PIN_LENGTH = 4;
const emptyPin = () => Array(PIN_LENGTH).fill("");

const SetMembershipPasswordPopup = ({
	membershipId,
	onClose,
	onSuccess,
	onSkip,
	onPasswordAlreadySet,
	showSkipForNow = false,
	showSuccessToast = true,
}) => {
	const [password, setPassword] = useState(emptyPin);
	const [confirmPassword, setConfirmPassword] = useState(emptyPin);
	const [formError, setFormError] = useState("");
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isPortalMounted, setIsPortalMounted] = useState(false);
	const passwordRefs = useRef([]);
	const confirmRefs = useRef([]);

	useEffect(() => {
		setIsPortalMounted(true);
		return () => setIsPortalMounted(false);
	}, []);

	useEffect(() => {
		if (!isPortalMounted) return;
		// Double rAF ensures the portal DOM is painted before focusing
		const t = requestAnimationFrame(() => {
			requestAnimationFrame(() => {
				passwordRefs.current[0]?.focus();
			});
		});
		return () => cancelAnimationFrame(t);
	}, [isPortalMounted]);

	const handleInput = useCallback((setter, refs, index, value, nextRefs) => {
		setFormError("");
		const sanitized = (value || "").replace(/[^0-9]/g, "").slice(-1);
		setter((prev) => {
			const next = [...prev];
			next[index] = sanitized;
			return next;
		});
		if (sanitized) {
			if (index < PIN_LENGTH - 1) {
				refs.current[index + 1]?.focus();
			} else if (nextRefs) {
				nextRefs.current[0]?.focus();
			}
		}
	}, []);

	const handleKeyDown = useCallback((source, refs, index, event) => {
		if (event.key === "Enter") {
			event.preventDefault();
			event.currentTarget?.form?.requestSubmit();
			return;
		}
		if (event.key === "Backspace" && !source[index] && index > 0) {
			refs.current[index - 1]?.focus();
		}
	}, []);

	const handleClose = () => {
		setPassword(emptyPin());
		setConfirmPassword(emptyPin());
		setFormError("");
		onClose?.();
	};

	const handleSkip = () => {
		setPassword(emptyPin());
		setConfirmPassword(emptyPin());
		setFormError("");
		if (onSkip) {
			onSkip();
		} else {
			onClose?.();
		}
	};

	const handleSubmit = async (event) => {
		event?.preventDefault();
		if (isSubmitting) return;

		const pin = password.join("");
		const confirmPin = confirmPassword.join("");
		setFormError("");

		if (pin.length !== PIN_LENGTH) {
			setFormError("Please enter a 4-digit password.");
			passwordRefs.current[0]?.focus();
			return;
		}
		if (confirmPin.length !== PIN_LENGTH) {
			setFormError("Please confirm your password.");
			confirmRefs.current[0]?.focus();
			return;
		}
		if (pin !== confirmPin) {
			setFormError("Passwords do not match.");
			confirmRefs.current[0]?.focus();
			return;
		}

		const idNum = Number(membershipId);
		if (!Number.isFinite(idNum) || idNum <= 0) {
			setFormError("Membership is missing. Close and try again.");
			return;
		}

		setIsSubmitting(true);
		try {
			const response = await setMembershipPin(idNum, {
				pin,
				confirm_pin: confirmPin,
			});
			const isSuccess = Boolean(response?.success || response?.data?.success);
			if (!isSuccess) {
				const apiMessage =
					response?.message || response?.data?.message || "Unable to set membership password.";
				if (apiMessage.toLowerCase().includes("different from the current") && typeof onPasswordAlreadySet === "function") {
					onPasswordAlreadySet();
					return;
				}
				setFormError(apiMessage);
				return;
			}
			if (showSuccessToast) {
				showAppToast("success", "Membership password set successfully.");
			}
			setPassword(emptyPin());
			setConfirmPassword(emptyPin());
			setFormError("");
			onSuccess?.();
		} catch (error) {
			const apiMessage =
				error?.response?.data?.message || "Unable to set membership password.";
			if (apiMessage.toLowerCase().includes("different from the current") && typeof onPasswordAlreadySet === "function") {
				onPasswordAlreadySet();
				return;
			}
			setFormError(apiMessage);
		} finally {
			setIsSubmitting(false);
		}
	};

	// Outside (overlay) click is intentionally disabled — the popup only closes
	// via the Cancel/Skip button or a successful submit.
	const handleOverlayClick = undefined;

	// Prevent the overlay from stealing focus from the PIN inputs on outside click.
	// Only block when the overlay itself is the target (not the inputs/buttons inside),
	// otherwise clicks inside the popup would also be unable to focus.
	const handleOverlayMouseDown = (e) => {
		if (e.target === e.currentTarget) {
			e.preventDefault();
		}
	};

	if (!isPortalMounted) {
		return null;
	}

	const modalContent = (
		<div className={`${styles.overlay} customModalOverlay`} onClick={handleOverlayClick} onMouseDown={handleOverlayMouseDown}>
			<div className={styles.popup} onClick={(e) => e.stopPropagation()}>
				<h3 className={styles.title}>Set Membership Password</h3>

				<form className={styles.form} autoComplete="off" noValidate onSubmit={handleSubmit}>
					<MembershipPinAutofillTrap />
					<div className={styles.fieldsBlock}>
						<div className={styles.fieldGroup}>
							<label className={styles.fieldLabel} htmlFor="set-membership-pin-0">
								Enter Password
							</label>
							<div className={styles.inputs}>
								{password.map((value, index) => (
									<input
										key={`set-pin-${index}`}
										id={index === 0 ? "set-membership-pin-0" : undefined}
										{...getMembershipPinInputProps("set", index)}
										maxLength={1}
										value={value}
										className={styles.input}
										ref={(el) => {
											passwordRefs.current[index] = el;
										}}
										onChange={(e) =>
											handleInput(
												setPassword,
												passwordRefs,
												index,
												e.target.value,
												confirmRefs
											)
										}
										onKeyDown={(e) => handleKeyDown(password, passwordRefs, index, e)}
									/>
								))}
							</div>
						</div>

						<div className={styles.fieldGroup}>
							<label className={styles.fieldLabel} htmlFor="set-membership-confirm-pin-0">
								Confirm Password
							</label>
							<div className={styles.inputs}>
								{confirmPassword.map((value, index) => (
									<input
										key={`set-pin-confirm-${index}`}
										id={index === 0 ? "set-membership-confirm-pin-0" : undefined}
										{...getMembershipPinInputProps("set-confirm", index)}
										maxLength={1}
										value={value}
										className={styles.input}
										ref={(el) => {
											confirmRefs.current[index] = el;
										}}
										onChange={(e) =>
											handleInput(
												setConfirmPassword,
												confirmRefs,
												index,
												e.target.value
											)
										}
										onKeyDown={(e) =>
											handleKeyDown(confirmPassword, confirmRefs, index, e)
										}
									/>
								))}
							</div>
						</div>
					</div>
					<div className={styles.formErrorSlot} role="alert" aria-live="polite">
						{formError ? <span className={styles.formErrorText}>{formError}</span> : null}
					</div>

					<div className={styles.actions}>
						<button
							type="button"
							className={showSkipForNow ? styles.skipButton : styles.cancelButton}
							onClick={showSkipForNow ? handleSkip : handleClose}
						>
							{showSkipForNow ? "Skip for now" : "Cancel"}
						</button>
						<button type="submit" className={styles.setButton} disabled={isSubmitting}>
							{isSubmitting ? "Saving..." : "Set Password"}
						</button>
					</div>
				</form>
			</div>
		</div>
	);

	return createPortal(modalContent, document.body);
};

export default SetMembershipPasswordPopup;
